import React, {
    useState, useEffect,
    createContext, useContext,
    useRef, forwardRef,
    useCallback
} from 'react'
import { mergeRefs } from 'react-merge-refs'
import { useIntl } from 'react-intl'
import { promptify } from 'utilities/prompt'
import { cls } from 'utilities/dom'
import { size } from 'utilities/object'
import {
    Wrapper, Container, Content,
    PromptHeader, Heading,
    PromptBody, PromptFooter
} from './s'
import Paragraph from 'components/typography/paragraph'
import Form from 'components/form/controller'
import Divider from 'components/divider'
import Acknowledge from 'components/acknowledge'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'

const PromptContext = createContext()

const Prompt = forwardRef(({ show, salt, ...props }, forwardedRef) => {
    const prompt = useRef()

    const [showing, setShowing] = useState(show)

    const handleOpen = useCallback(() => {
        setShowing(true)
        prompt.current?.showModal()
    }, [])

    const handleClose = useCallback(() => {
        const handleAnimationEnd = () => {
            setShowing(false)
            prompt.current.removeEventListener('animationend', handleAnimationEnd)
        }

        prompt.current.addEventListener('animationend', handleAnimationEnd)
        props.cancel?.callback?.()
        prompt.current.close()
    }, [props.cancel])

    useEffect(() => {
        if(prompt.current) {
            const controller = new AbortController()

            prompt.current.addEventListener('open', handleOpen, { signal: controller.signal })
            prompt.current.addEventListener('close', handleClose, { signal: controller.signal })

            return () => controller.abort()
        }
    }, [handleOpen, handleClose])

    useEffect(() => {
        if(!!show) {
            handleOpen()
        } else if(prompt.current?.open) {
            handleClose()
        }
    }, [show, handleOpen, handleClose])

    return promptify(
        <PromptContext.Provider value={{
            show,
            showing,
            ref: mergeRefs([forwardedRef, prompt])
        }}>
            <Wrapper
                ref={prompt}
                key={`prompt:standard:${salt}`}>
                {!!show && (
                    <PromptContent
                        {...props}
                        salt={salt} />
                )}
            </Wrapper>
        </PromptContext.Provider>
    )
})

const PromptContent = ({ className, salt, ...props }) => {
    const { formatMessage } = useIntl()

    const {
        question = formatMessage({
            id: 'prompt_question',
            defaultMessage: 'Are you sure?'
        }),
        message = null,
        children = null,
        acknowledge = null,
        cancel,
        confirm,
        loading = false
    } = props

    const {
        text: cancelText = formatMessage({
            id: 'action_cancel',
            defaultMessage: 'Cancel'
        }),
        callback: onCancel
    } = cancel

    const {
        text: confirmText = formatMessage({
            id: 'action_continue',
            defaultMessage: 'Continue'
        }),
        callback: onConfirm,
        effect = 'neutral'
    } = confirm

    const submit = () => {
        onConfirm()
        onCancel()
    }

    return (
        <Container {...(className ? { className } : null)}>
            <Content>
                <Prompt.Header className="prompt-header">{question}</Prompt.Header>
                <Form
                    layout="vertical"
                    onSubmit={submit}>
                    {({ errors, trigger }) => (
                        <>
                            {(!!message || children || acknowledge) && (
                                <Prompt.Body className="prompt-body">
                                    {!!message && (
                                        <Paragraph className="preserve-line-breaks">{message}</Paragraph>
                                    )}
                                    {children}
                                    {acknowledge && (
                                        <>
                                            <Divider />
                                            <Acknowledge
                                                {...acknowledge}
                                                salt={`${salt}:acknowledge`} />
                                        </>
                                    )}
                                </Prompt.Body>
                            )}
                            <Prompt.Footer className="prompt-footer">
                                <Actions {...(!confirm ? { className: 'centered' } : null)}>
                                    <Plain
                                        className="neutral"
                                        onClick={onCancel}
                                        autoFocus={!acknowledge}>
                                        {cancelText}
                                    </Plain>
                                    {!!confirmText && (
                                        <ButtonSubmit
                                            className={cls([
                                                effect,
                                                loading && 'loading'
                                            ])}
                                            disabled={!!size(errors) || loading}
                                            ref={trigger}>
                                            {confirmText}
                                        </ButtonSubmit>
                                    )}
                                </Actions>
                            </Prompt.Footer>
                        </>
                    )}
                </Form>
            </Content>
        </Container>
    )
}

Prompt.Header = ({ children, className, ...props }) => (
    <PromptHeader
        {...props}
        className={cls([
            className,
            'prompt-header'
        ])}>
        <Heading>
            {children}
        </Heading>
    </PromptHeader>
)

Prompt.Body = ({ children, className, ...props }) => (
    <PromptBody
        {...props}
        className={cls([
            className,
            'prompt-body'
        ])}>
        {children}
    </PromptBody>
)

Prompt.Footer = ({ children, className, ...props }) => (
    <PromptFooter
        {...props}
        className={cls([
            className,
            'prompt-footer'
        ])}>
        {children}
    </PromptFooter>
)

export const usePrompt = () => useContext(PromptContext)
export default Prompt